import React, { Component } from 'react';
import { t, getLang } from '../i18n';
import nativeBridge, { openLink } from '../widget/native-bridge.js';
import { KB, KB_ARTICLE, kbArticleCssText } from '../widget/component-contract';

// 正文排版表编译成的 CSS，模块级只算一次（KB_ARTICLE 是 frozen 常量表，
// 值不会随渲染变；每次 render 重算是白烧 CPU）。
const KB_ARTICLE_CSS = kbArticleCssText('.lc-kb-article');

export default class KnowledgeDetail extends Component {
    // KB 正文内链接委托点击：仅 native 容器且 openUrl 就绪时接管；web 端保持 <a target=_blank> 原生行为。
    handleArticleLinkClick = (e) => {
        // 与 message-area.js 的 handleContentLinkClick 保持一致：内层 <a onClick> 已自行
        // openLink 并 preventDefault 时不要再冒泡触发第二次 call:openUrl。
        if (e.defaultPrevented) return;
        if (!(nativeBridge.isActive() && nativeBridge.supports('openUrl'))) return;
        const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
        if (!a) return;
        const href = a.getAttribute('href');
        if (!href || href === '#') return;
        if (!/^(https?:\/\/|mailto:|tel:)/i.test(href)) return;
        e.preventDefault();
        openLink(href, { source: 'kb_article' });
    };

    render() {
        const { article, onBack } = this.props;

        if (!article) {
            return <div>{t('know.article_not_found')}</div>;
        }

        // 确保关键字段是字符串，防止渲染对象导致错误
        const safeArticle = {
            ...article,
            title: typeof article.title === 'object' && article.title.name
                ? article.title.name
                : (article.title || t('know.untitled')),
            content: article.content || '',
        };

        const containerStyle = {
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'var(--lc-surface, #fff)',
            overflow: 'hidden',
        };

        // 设计语言（Crisp 结构）：左上角圆形返回按钮（< 箭头，主色实心）+ 标题居中。
        const headerStyle = {
            padding: 'var(--lc-space-2) var(--lc-space-3)',
            borderBottom: 'var(--lc-hairline)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--lc-space-2)',
            flexShrink: 0,
        };

        // 幽灵图标钮,与同屏右侧的外链钮**逐字段同款**。
        // 此前是品牌色实心圆 + 白箭头:同一行里最平常、最高频的"返回"被画成了
        // 整屏最响的元素,比旁边真正的内容操作还重,而 widget 自己 header 上的
        // 关闭/列表钮又是另一套(radius-sm + 半透明)。三种按钮语言同屏出现。
        // 形状保持 radius-full —— 变的是"响度"不是几何。
        const backButtonStyle = {
            width: `${KB.backButtonPx}px`,
            height: `${KB.backButtonPx}px`,
            borderRadius: 'var(--lc-radius-full)',
            border: 'var(--lc-hairline)',
            background: 'transparent',
            color: 'var(--lc-text-2, #64748b)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            padding: 0,
            transition: 'background var(--lc-dur-fast) var(--lc-ease-out), color var(--lc-dur-fast) var(--lc-ease-out)',
        };

        const titleBarStyle = {
            flex: 1,
            minWidth: 0,
            textAlign: 'center',
            fontSize: `${KB.rowTitleSizePx}px`,
            fontWeight: '600',
            color: 'var(--lc-text, #1e293b)',
            lineHeight: '1.35',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            userSelect: 'none',
        };

        const contentWrapperStyle = {
            flex: '1 1 auto',
            // minHeight:0 —— 与 home / 会话列表同一套定案：flex 列里 overflow 生效的前提。
            // 返回栏是 flex-shrink:0，正文若被内容顶高就会溢出 overflow:hidden 的外层，
            // 文章末尾直接够不到。
            minHeight: 0,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            // 设计语言：详情正文四周内边距（Crisp 满宽排版是已知缺点，不抄）。
            // 取值来自跨端排版表 KB_ARTICLE —— 三端注入同一张表。
            padding: `${KB_ARTICLE.paddingPx}px`,
        };

        const articleTitleStyle = {
            fontSize: `${KB_ARTICLE.h1SizePx}px`,
            fontWeight: KB_ARTICLE.h1Weight,
            color: 'var(--lc-text, #1e293b)',
            margin: `0 0 ${KB_ARTICLE.h1MarginBottomPx}px`,
            lineHeight: KB_ARTICLE.h1LineHeight,
        };

        const metaInfoStyle = {
            display: 'flex',
            gap: 'var(--lc-space-4)',
            alignItems: 'center',
            marginBottom: 'var(--lc-space-6)',
            fontSize: '13px',
            color: 'var(--lc-text-3, #94a3b8)',
        };

        const categoryBadgeStyle = {
            display: 'inline-block',
            padding: `${KB_ARTICLE.badgePadVPx}px ${KB_ARTICLE.badgePadHPx}px`,
            // 主色 8.2% 底 + 主色字。用 token 而不是 `${color}15` 字符串拼接：
            // 主色可能带透明度（rgba()/8 位 hex），拼接会产出非法 CSS、整条声明被丢掉。
            backgroundColor: `var(${KB_ARTICLE.badgeBgToken})`,
            color: `var(${KB_ARTICLE.badgeColorToken})`,
            borderRadius: `${KB_ARTICLE.badgeRadiusPx}px`,
            fontSize: `${KB_ARTICLE.badgeSizePx}px`,
            fontWeight: KB_ARTICLE.badgeWeight,
        };

        const tagsStyle = {
            display: 'flex',
            gap: `${KB_ARTICLE.tagsGapPx}px`,
            marginTop: `${KB_ARTICLE.tagsMarginTopPx}px`,
            paddingTop: `${KB_ARTICLE.tagsPaddingTopPx}px`,
            borderTop: 'var(--lc-hairline)',
            flexWrap: 'wrap',
        };

        const tagStyle = {
            padding: `${KB_ARTICLE.tagPadVPx}px ${KB_ARTICLE.tagPadHPx}px`,
            backgroundColor: `var(${KB_ARTICLE.tagBgToken}, #f1f5f9)`,
            borderRadius: `${KB_ARTICLE.tagRadiusPx}px`,
            fontSize: `${KB_ARTICLE.tagSizePx}px`,
            color: `var(${KB_ARTICLE.tagColorToken}, #64748b)`,
        };

        return (
            <div style={containerStyle}>
                {/* Header：圆形返回按钮（<）+ 文章标题居中 + 右侧对称占位 */}
                <div style={headerStyle}>
                    <button
                        style={backButtonStyle}
                        onClick={onBack}
                        aria-label={t('know.back')}
                        title={t('know.back')}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--lc-surface-2, #f1f5f9)'; e.currentTarget.style.color = 'var(--lc-text, #1e293b)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--lc-text-2, #64748b)'; }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                    </button>
                    <div style={titleBarStyle} title={safeArticle.title}>
                        {safeArticle.title}
                    </div>
                    {/* 右上角「在新窗口打开」外链（仅当 app 配置了知识库公开站时渲染；
                        与左侧返回按钮对称，但更轻：无实心底，hover 浅底） */}
                    {(() => {
                        const cfg = this.props.appConfig || {};
                        const kbBase = cfg.kb_public_base_url
                            || (cfg.widget_config && cfg.widget_config.kb_public_base_url)
                            || null;
                        if (!kbBase || !article.id) {
                            // 与返回按钮等宽的占位，保证标题真正居中
                            return <div style={{ width: `${KB.backButtonPx}px`, flexShrink: 0 }} aria-hidden="true" />;
                        }
                        const url = `${String(kbBase).replace(/\/+$/, '')}/article/${encodeURIComponent(article.id)}`;
                        return (
                            <button
                                style={{
                                    width: `${KB.backButtonPx}px`, height: `${KB.backButtonPx}px`, borderRadius: 'var(--lc-radius-full)',
                                    border: 'var(--lc-hairline)', background: 'transparent',
                                    color: 'var(--lc-text-2, #64748b)', cursor: 'pointer', padding: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0, transition: 'background var(--lc-dur-fast) var(--lc-ease-out), color var(--lc-dur-fast) var(--lc-ease-out)',
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    try { window.open(url, '_blank', 'noopener,noreferrer'); } catch (err) { /* ignore */ }
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--lc-surface-2, #f1f5f9)'; e.currentTarget.style.color = 'var(--lc-text, #1e293b)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--lc-text-2, #64748b)'; }}
                                aria-label={t('know.open_external')}
                                title={t('know.open_external')}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                    <polyline points="15 3 21 3 21 9" />
                                    <line x1="10" y1="14" x2="21" y2="3" />
                                </svg>
                            </button>
                        );
                    })()}
                </div>

                {/* Article Content */}
                <div style={contentWrapperStyle}>
                    <h1 style={articleTitleStyle}>{safeArticle.title}</h1>

                    <div style={metaInfoStyle}>
                        {safeArticle.category && (
                            <span style={categoryBadgeStyle}>
                                {typeof safeArticle.category === 'object' && safeArticle.category.name
                                    ? safeArticle.category.name
                                    : safeArticle.category}
                            </span>
                        )}
                        {safeArticle.created_at && (
                            <span>{t('know.published_on', {
                                date: new Date(safeArticle.created_at).toLocaleDateString(getLang() === 'zh' ? 'zh-CN' : 'en-US')
                            })}</span>
                        )}
                    </div>

                    {/* 正文排版：此前是裸 dangerouslySetInnerHTML，**一条标签规则都没有**
                        —— h1 和正文一样大、代码块没有底、表格没有边框。Android 早就注入了
                        一份 CSS、iOS 注入了另一份，同一篇文章三端读起来是三个排版。
                        现在三端共用 KB_ARTICLE 这张表（sdk/shared/design-tokens.json
                        的 components.kbArticle）。 */}
                    <style>{KB_ARTICLE_CSS}</style>
                    <div
                        className="lc-kb-article"
                        onClick={this.handleArticleLinkClick}
                        dangerouslySetInnerHTML={{ __html: safeArticle.content }}
                    />

                    {/* Tags */}
                    {(() => {
                        let tags = [];
                        if (safeArticle.tags) {
                            if (Array.isArray(safeArticle.tags)) {
                                tags = safeArticle.tags;
                            } else if (typeof safeArticle.tags === 'string') {
                                try {
                                    tags = JSON.parse(safeArticle.tags);
                                    if (!Array.isArray(tags)) tags = [];
                                } catch(e) {
                                    tags = [];
                                }
                            }
                        }

                        // 确保 tags 是字符串数组，过滤掉对象或其他非字符串类型
                        tags = tags.filter(tag => {
                            const tagType = typeof tag;
                            return tagType === 'string' || tagType === 'number';
                        }).map(tag => String(tag));

                        return tags.length > 0 && (
                            <div style={tagsStyle}>
                                {tags.map((tag, idx) => (
                                    <span key={idx} style={tagStyle}>#{tag}</span>
                                ))}
                            </div>
                        );
                    })()}
                </div>
            </div>
        );
    }
}